import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MagnifyingGlass, Storefront, TrendDown, CircleNotch, Sparkle, Tag } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";
import { Card, MerchantLogo } from "@/components/riq";

export default function PriceCompare() {
  const { user } = useAuth();
  const cur = user?.currency || "ZAR";
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popular, setPopular] = useState([]);
  const [searched, setSearched] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/prices/popular").then((r) => setPopular(r.data.items || [])).catch(() => {});
  }, []);

  const runSearch = async (term) => {
    const value = (term ?? q).trim();
    if (value.length < 2) { setResults(null); setSearched(false); return; }
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api.get(`/prices/search?q=${encodeURIComponent(value)}`);
      setResults(data.results || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  };

  const onChange = (v) => {
    setQ(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => runSearch(v), 450);
  };

  const pickPopular = (name) => { setQ(name); runSearch(name); };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Community prices</div>
        <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary mt-1">Where's it cheapest?</h1>
        <p className="text-muted-foreground mt-1">Compare live online retailer prices alongside anonymous receipts from the Sliply community.</p>
      </div>

      <div className="relative">
        <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input data-testid="price-search-input" value={q} onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && runSearch()}
          placeholder="Search an item, e.g. milk, bread, coffee…"
          className="w-full bg-white rounded-full border border-riq-border pl-12 pr-4 py-3.5 text-sm outline-none focus:border-riq-primary" />
        {loading && <CircleNotch size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-riq-primary animate-spin" />}
      </div>

      {!searched && popular.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5"><Sparkle size={14} weight="fill" className="text-riq-gold" /> Popular right now</div>
          <div className="flex flex-wrap gap-2" data-testid="price-popular">
            {popular.map((p) => (
              <button key={p.item} data-testid={`price-popular-${p.item}`} onClick={() => pickPopular(p.item)}
                className="rounded-full border border-riq-border bg-white px-3.5 py-2 text-sm font-medium hover:border-riq-primary hover:-translate-y-0.5 transition-all">
                {p.item} <span className="text-xs text-muted-foreground">· {p.merchant_count} stores</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {searched && !loading && results?.length === 0 && (
        <Card className="text-center py-10" data-testid="price-no-results">
          <Storefront size={32} weight="duotone" className="text-neutral-300 mx-auto mb-3" />
          <div className="font-medium">No prices for "{q}" yet</div>
          <p className="text-sm text-muted-foreground mt-1">Try a simpler word (like "milk"), or scan more receipts to grow the data.</p>
        </Card>
      )}

      <div className="space-y-4">
        {(results || []).map((r, idx) => (
          <motion.div key={r.item} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
            <Card data-testid={`price-result-${r.item}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-head font-bold text-riq-primary truncate">{r.item}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {r.merchant_count} price{r.merchant_count === 1 ? "" : "s"}
                    {r.online_count > 0 && ` · ${r.online_count} online`}
                    {r.community_count > 0 && ` · ${r.community_count} community`}
                  </div>
                </div>
                {r.max_savings > 0 && (
                  <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#E4F7ED] text-[#1f7a48] px-2.5 py-1 text-xs font-bold">
                    <TrendDown size={13} weight="bold" /> Save {money(r.max_savings, cur)}
                  </span>
                )}
              </div>
              <div className="mt-4 space-y-2">
                {r.prices.map((p, i) => {
                  const cheapest = i === 0;
                  const online = p.source === "online";
                  const RowTag = online && p.url ? "a" : "div";
                  return (
                    <RowTag key={`${p.merchant}-${i}`}
                      {...(online && p.url ? { href: p.url, target: "_blank", rel: "noopener noreferrer" } : {})}
                      data-testid={`price-row-${r.item}-${p.merchant}`}
                      className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${cheapest ? "border-riq-primary bg-riq-primary/5" : "border-riq-border"} ${online && p.url ? "hover:-translate-y-0.5 transition-transform" : ""}`}>
                      {online && p.image_url
                        ? <img src={p.image_url} alt="" className="w-9 h-9 rounded-lg object-contain bg-secondary shrink-0" onError={(e) => { e.target.style.visibility = "hidden"; }} />
                        : <MerchantLogo name={p.merchant} size={34} />}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm truncate flex items-center gap-2">
                          {p.merchant}
                          {online && <span className="rounded-full bg-riq-sage text-riq-primary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide">Online</span>}
                          {cheapest && <span className="rounded-full bg-riq-primary text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">Cheapest</span>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {online
                            ? (p.title || "In-store online") + (p.in_stock === false ? " · lead time" : "")
                            : `latest ${shortDate(p.date)} · ${p.count} slip${p.count === 1 ? "" : "s"}`}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className={`font-head font-bold ${cheapest ? "text-riq-primary" : "text-slate-700"}`}>{money(p.price, cur)}</div>
                        {online && p.was_price && <div className="text-[11px] text-muted-foreground line-through">{money(p.was_price, cur)}</div>}
                      </div>
                    </RowTag>
                  );
                })}
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground text-center flex items-center justify-center gap-1.5">
        <Tag size={12} /> Live online prices plus the most recent community slip price per store (last 60 days). Prices may vary; data is anonymised.
      </p>
    </div>
  );
}
